import React, { useEffect, useState } from 'react';
import { Button, Select } from 'antd';
import {
  AkamProLogoIcon,
  ArrowDownIcon,
  CloseXIcon,
  HeadphoneIcon,
  InfoCircleIcon,
  TaskSquareIcon,
  TimerIcon
} from 'icons';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppSelector } from 'store';

import { ISecondRoot, useUniversityCourseSelect } from 'modules/global';

import { Modal, Visible } from 'components';

import { useStartPrivateTest } from '../hooks';

interface IProps {
  open: boolean;
  onClose: () => void;
  courseId?: string;
  universityId?: string;
  universityImage?: string;
  universityName?: string;
  universityLogo?: string;
}

const UniversitySelectModal: React.FC<IProps> = ({
  open,
  onClose,
  courseId,
  universityId,
  universityImage,
  universityName,
  universityLogo
}) => {
  const t = useTranslations();
  const { query } = useRouter();
  const { univerCourse, isLoading } = useUniversityCourseSelect(universityId);
  const [selectedUniCourse, setSelectedUniCourse] = useState<ISecondRoot | undefined>();
  const { mutate, isLoading: startTestLoading } = useStartPrivateTest();
  const isRouteLoading = useAppSelector(state => state.routeLoading.isRouteLoading);
  const [step, setStep] = useState(1);

  useEffect(() => {
    if (open && courseId && univerCourse?.length) {
      const selected = univerCourse.find(item => item.id === courseId);

      setSelectedUniCourse(selected);
      setStep(2);
    } else if (!courseId) {
      setSelectedUniCourse(undefined);
    }
  }, [open, courseId, univerCourse]);

  const onStartTest = () => {
    const rawData = {
      universitySlug: query?.universityId as string,
      courseSlug: selectedUniCourse?.slug
    };

    mutate(rawData);
  };

  return (
    <Modal
      open={open}
      centered
      onClose={onClose}
      width={800}
      closeIcon={false}
      styles={{
        content: { padding: '0', borderRadius: '12px' }
      }}
    >
      <div className="sm:h-110 flex flex-col items-center sm:flex-row">
        <div className="min-h-110 relative hidden w-1/2 items-center justify-center sm:flex">
          <Image
            src={step === 1 ? universityImage || '/images/writing-man.webp' : '/images/writing-man.webp'}
            alt="Users"
            fill
            className="w-full rounded-bl-xl rounded-tl-xl object-cover"
            title="Xususiy universitet imtixonini boshlash"
            loading="lazy"
          />
          <div className="z-5 absolute bottom-1.5 left-2">
            <AkamProLogoIcon width={150} height={35} />
          </div>
        </div>
        <div className="h-full w-full sm:w-1/2 ">
          <div className="m-1 flex hidden cursor-pointer items-center justify-end sm:flex" onClick={onClose}>
            <div className="p-1 hover:rounded-full hover:bg-gray-100">
              <CloseXIcon size="18" color="#4B5563" />
            </div>
          </div>
          <div className="p-2 md:px-4 md:py-0">
            {step === 1 ? (
              <div className="h-95 flex flex-1 flex-col justify-between">
                <div>
                  <div className="flex flex-col items-center justify-center space-y-4 text-center sm:flex-row sm:items-center sm:justify-normal sm:space-x-2 sm:space-y-0 sm:text-left">
                    {universityLogo ? (
                      <div className="max-h-15 min-h-15 min-w-15 max-w-15 relative overflow-hidden rounded-full">
                        <Image
                          fill
                          className="object-cover"
                          src={universityLogo}
                          alt={universityName?.slice(0, 10) || 'University Logo'}
                          title="Universitet logosi"
                        />
                      </div>
                    ) : (
                      <div className="max-h-15 min-h-15 min-w-15 max-w-15 relative flex items-center justify-center overflow-hidden rounded-full bg-gray-50">
                        <Image
                          width={15}
                          height={15}
                          className="object-cover"
                          src="/images/default-no-image.webp"
                          alt={universityName?.slice(0, 10) || 'University Logo'}
                          title="Universitet logosi"
                        />
                      </div>
                    )}
                    <h2 className="md:text-md text-sm font-semibold">{universityName}</h2>
                  </div>
                  <div className="mt-6 flex gap-2 rounded-lg bg-yellow-50 p-3">
                    <div className="mt-1 flex-shrink-0 text-yellow-400">
                      <InfoCircleIcon />
                    </div>
                    <div className="">
                      <h4 className="mb-1 font-medium">{t('testni_boshlashingizdan_oldin_muhim_eslatma')}</h4>
                      <p className="text-xs">
                        {t.rich('testni_boshlashingizdan_oldin_muhim_eslatma_desc', {
                          universityName: universityName ?? 'Universitet',
                          b: chunks => <b>{chunks}</b>
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                  <Button
                    onClick={onClose}
                    className="!rounded-lg !border-transparent !bg-gray-200 !py-4 !font-medium !text-gray-700"
                  >
                    {t('common.ortga_qaytish')}
                  </Button>
                  <Button
                    onClick={() => setStep(2)}
                    className="!rounded-lg !border-transparent !bg-green-500 !py-4 !text-white"
                  >
                    {t('davom_ettirish')}
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label className="text-gray-800">{t('Yonalishni_tanlang')}</label>
                  <Select
                    loading={isLoading}
                    value={selectedUniCourse?.id}
                    autoFocus
                    onChange={value => {
                      const selected = univerCourse?.find(item => item.id === value);

                      setSelectedUniCourse(selected);
                    }}
                    suffixIcon={<ArrowDownIcon />}
                    className="custom-select !mt-1 w-full"
                    placeholder={t('Yonalishlar')}
                    options={univerCourse?.map(d => ({
                      value: d.id,
                      label: d.course_name
                    }))}
                    allowClear
                  />
                </div>

                <Visible
                  if={selectedUniCourse?.direction}
                  else={
                    <Visible
                      if={selectedUniCourse}
                      else={
                        <div className="min-h-38 my-4.5 flex items-center justify-center rounded-xl border border-gray-200 text-xs text-gray-600 lg:text-sm">
                          {t('Yonalishingiz_boyicha_fanlarni_tanlang')}
                        </div>
                      }
                    >
                      <div className="min-h-38 my-4.5 flex items-center justify-center rounded-xl border border-gray-200 text-sm text-gray-600">
                        {t('Ushbu_yonalish_uchun_fanlar_mavjud_emas')}
                      </div>
                    </Visible>
                  }
                >
                  <div className="max-h-38 my-5 space-y-2 overflow-auto rounded-xl border border-gray-200 p-2">
                    {selectedUniCourse?.direction?.subjects?.map((subject, index) => (
                      <div
                        key={subject.id}
                        className="flex items-center justify-between rounded-xl bg-gray-50 p-3 py-2"
                      >
                        <p className="font-medium">
                          {index + 1}. {subject.name}
                        </p>
                        <p>
                          {subject.question_count} {t('ta')}
                        </p>
                      </div>
                    ))}
                  </div>
                </Visible>

                <div className="flex items-center gap-3 rounded-xl bg-yellow-50 p-3">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-yellow-100 text-yellow-400">
                    <HeadphoneIcon />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium">{t('eslatma')}!</h4>
                    <p className="text-xs text-gray-800">
                      {t('test_yakunida_ariza_qoldirsangiz_sizga_operatorla_boglanishadi_va_toliq_malumot_berishadi')}
                    </p>
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    {selectedUniCourse && (
                      <div className="flex items-center gap-3 font-medium">
                        <div className="flex items-center gap-1">
                          <TaskSquareIcon />
                          {selectedUniCourse?.direction?.total_question_count}
                          {t('ta')}
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="text-indigo-600">
                            <TimerIcon />
                          </div>
                          {selectedUniCourse?.direction?.duration} {t('daqiqa')}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-x-2">
                    {!selectedUniCourse && (
                      <Button
                        onClick={onClose}
                        className="!rounded-lg !border-transparent !bg-gray-100 !py-4 !font-medium !text-gray-700"
                      >
                        {t('common.ortga_qaytish')}
                      </Button>
                    )}
                    <Button
                      disabled={!selectedUniCourse}
                      loading={startTestLoading || isRouteLoading}
                      onClick={onStartTest}
                      className="!rounded-lg !border-transparent !bg-green-500 !py-4 !text-white disabled:!bg-gray-100 disabled:!text-gray-400"
                    >
                      {t('boshlash')}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UniversitySelectModal;
